import { z } from 'zod';
import { compareMoney, formatMoney, isZeroMoney, moneyAmountSchema, subtractMoney, } from './money.js';
import { positiveAmountSchema } from './transactions.js';
/**
 * Installment plans (Phase 4 PR 2, docs/plans/phase-4.md). A plan says how a debt is paid back:
 * an amount every month on a day of the month, a fixed number of times or until it is paid.
 * The app never moves money on its own; each installment is paid with the normal payment form.
 *
 * Installments are numbered 1, 2, 3, ... and fall one month apart. A plan has versions: a change
 * starts a new version from the next installment that is neither paid nor skipped, so paid and
 * skipped installments keep the terms they had. Due dates and states are computed from the plan,
 * the paid and skipped marks, what is outstanding and today's date in the family time zone, so
 * nothing needs storing to know what is due (reminders and later push notifications included).
 *
 * The amount of an installment is the part that lowers the debt; interest or fees (repaying an
 * outside person) are added on top when paying.
 */
export const INSTALLMENT_STATES = ['paid', 'skipped', 'overdue', 'due_today', 'upcoming'];
export const installmentStateSchema = z.enum(INSTALLMENT_STATES);
/** A calendar month, `YYYY-MM`. */
export const calendarMonthSchema = z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Use the format YYYY-MM');
/** The most installments one plan version may have (50 years, monthly). */
export const MAX_INSTALLMENTS = 600;
/**
 * PUT /api/v1/debts/:id/plan: create the plan, or change it from the next open installment on.
 * `count` is the number of installments still to pay under these terms (skipped ones are added
 * at the end); null means "until paid". `startMonth` is the month of the first installment under
 * these terms; on a change it defaults to the month the next installment was due anyway.
 */
export const installmentPlanRequestSchema = z.object({
    amount: positiveAmountSchema,
    dayOfMonth: z.number().int().min(1).max(31),
    startMonth: calendarMonthSchema.optional(),
    count: z.number().int().min(1).max(MAX_INSTALLMENTS).nullable(),
});
export const installmentSchema = z.object({
    number: z.number().int().positive(),
    /** YYYY-MM-DD; the last day of the month when the month is shorter than the plan's day. */
    dueOn: z.string(),
    /** The planned amount (the last one is smaller when less than that is still outstanding). */
    amount: moneyAmountSchema,
    state: installmentStateSchema,
    /** The payment that paid it. */
    transactionId: z.string().uuid().nullable(),
});
export const installmentPlanSchema = z.object({
    /** The terms in force now (the latest version). */
    amount: moneyAmountSchema,
    dayOfMonth: z.number().int(),
    /** Installments to pay under these terms; null: until paid. */
    count: z.number().int().nullable(),
    version: z.number().int(),
    /** Every installment, oldest first: paid, skipped, then the open ones. */
    installments: z.array(installmentSchema),
    /** Nothing left to pay (all paid, or the debt reached zero). */
    finished: z.boolean(),
});
/** What "Pay installment" fills in: the next open installment and the last payment's choices. */
export const installmentPrefillSchema = z.object({
    /** The money account of the last payment, when the viewer can still record on it. */
    accountId: z.string().uuid().nullable(),
    counterpartAccountId: z.string().uuid().nullable(),
    /** The interest or fee part of the last payment, if it had one. */
    interest: moneyAmountSchema.nullable(),
});
// ---------------------------------------------------------------------------------------------
// Dates: plain integer calendar arithmetic (no Date objects, so no time zone can shift a day).
// ---------------------------------------------------------------------------------------------
function parseMonth(month) {
    return {
        year: Number.parseInt(month.slice(0, 4), 10),
        month: Number.parseInt(month.slice(5, 7), 10),
    };
}
function pad(value, width) {
    return String(value).padStart(width, '0');
}
export function isLeapYear(year) {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}
/** Days in a month (1-12): February has 29 in leap years. */
export function daysInMonth(year, month) {
    if (month === 2)
        return isLeapYear(year) ? 29 : 28;
    return [4, 6, 9, 11].includes(month) ? 30 : 31;
}
/** `YYYY-MM` plus `n` months (n may be negative). */
export function addMonths(month, n) {
    const { year, month: m } = parseMonth(month);
    const index = year * 12 + (m - 1) + n;
    return `${pad(Math.floor(index / 12), 4)}-${pad((index % 12) + 1, 2)}`;
}
/** Months from `a` to `b` (positive when b is later). */
export function monthsBetween(a, b) {
    const x = parseMonth(a);
    const y = parseMonth(b);
    return (y.year - x.year) * 12 + (y.month - x.month);
}
/** The due date in `month` for a plan on `dayOfMonth`: short months fall on their last day. */
export function dueDateIn(month, dayOfMonth) {
    const { year, month: m } = parseMonth(month);
    return `${month}-${pad(Math.min(dayOfMonth, daysInMonth(year, m)), 2)}`;
}
/** The version that covers installment `number` (the latest whose first number is not after it). */
function versionFor(versions, number) {
    let found = versions[0];
    for (const v of versions)
        if (v.firstNumber <= number)
            found = v;
    return found;
}
/** Due date of installment `number` under the version that covers it. */
export function installmentDueOn(versions, number) {
    const v = versionFor(versions, number);
    return dueDateIn(addMonths(v.startMonth, number - v.firstNumber), v.dayOfMonth);
}
function stateOf(dueOn, today) {
    if (dueOn < today)
        return 'overdue';
    return dueOn === today ? 'due_today' : 'upcoming';
}
/**
 * Every installment of a plan, in order. `marks` are the paid (and not corrected) and skipped
 * installments. Open installments are listed while something is still outstanding: under the
 * latest terms, until `count` installments are paid or open (skipped ones do not count, so a
 * skip adds a month at the end), or, "until paid", until the open ones cover what is owed. The
 * last open installment is smaller when less than the plan amount is left. Once nothing is
 * outstanding there is nothing left to pay, whatever the plan said.
 */
export function buildSchedule(versions, marks, outstanding, today) {
    if (versions.length === 0)
        return [];
    const sorted = [...versions].sort((a, b) => a.version - b.version);
    const byNumber = new Map(marks.map((m) => [m.number, m]));
    const lastMarked = marks.reduce((max, m) => Math.max(max, m.number), 0);
    const out = [];
    let remaining = outstanding;
    sorted.forEach((v, i) => {
        const next = sorted[i + 1];
        const latest = next === undefined;
        let counted = 0;
        for (let n = v.firstNumber; latest || n < next.firstNumber; n++) {
            const full = latest && v.count !== null && counted >= v.count;
            const nothingLeft = compareMoney(remaining, '0') <= 0;
            if (latest && n > lastMarked && (full || nothingLeft))
                break;
            if (latest && n - v.firstNumber > MAX_INSTALLMENTS * 2)
                break;
            const dueOn = dueDateIn(addMonths(v.startMonth, n - v.firstNumber), v.dayOfMonth);
            const mark = byNumber.get(n);
            if (mark) {
                if (mark.kind === 'paid')
                    counted++;
                out.push({
                    number: n,
                    dueOn,
                    amount: formatMoney(v.amount),
                    state: mark.kind,
                    transactionId: mark.transactionId,
                });
                continue;
            }
            if (full || nothingLeft)
                continue;
            const amount = compareMoney(v.amount, remaining) > 0 ? remaining : v.amount;
            remaining = subtractMoney(remaining, amount);
            counted++;
            out.push({
                number: n,
                dueOn,
                amount: formatMoney(amount),
                state: stateOf(dueOn, today),
                transactionId: null,
            });
        }
    });
    return out;
}
/** The open installments (not paid or skipped), in order. */
export function openInstallments(schedule) {
    return schedule.filter((s) => s.state !== 'paid' && s.state !== 'skipped');
}
/** True when nothing is left to pay on the plan. */
export function isPlanFinished(schedule, outstanding) {
    return isZeroMoney(outstanding) || openInstallments(schedule).length === 0;
}
// ---------------------------------------------------------------------------------------------
// Recurring schedules (Phase 5 PR 1, docs/plans/phase-5.md). The same calendar rules as
// installments (a short month falls on its last day, plain dates in the family time zone), plus
// weekly schedules. A recurring template has versions: each version's own series covers the days
// from its `effectiveFrom` until the next version starts, so a change never touches occurrences
// before it. Paused periods have no occurrences at all (they are never backfilled).
// ---------------------------------------------------------------------------------------------
/** Days since 1970-01-01 of a `YYYY-MM-DD` date (proleptic Gregorian, no Date objects). */
export function dayNumber(date) {
    let y = Number.parseInt(date.slice(0, 4), 10);
    const m = Number.parseInt(date.slice(5, 7), 10);
    const d = Number.parseInt(date.slice(8, 10), 10);
    y -= m <= 2 ? 1 : 0;
    const era = Math.floor(y / 400);
    const yoe = y - era * 400;
    const doy = Math.floor((153 * (m + (m > 2 ? -3 : 9)) + 2) / 5) + d - 1;
    const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy;
    return era * 146097 + doe - 719468;
}
/** The `YYYY-MM-DD` date of a day number (inverse of `dayNumber`). */
export function dateOfDay(day) {
    const z = day + 719468;
    const era = Math.floor(z / 146097);
    const doe = z - era * 146097;
    const yoe = Math.floor((doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365);
    const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100));
    const mp = Math.floor((5 * doy + 2) / 153);
    const d = doy - Math.floor((153 * mp + 2) / 5) + 1;
    const m = mp + (mp < 10 ? 3 : -9);
    const y = yoe + era * 400 + (m <= 2 ? 1 : 0);
    return `${pad(y, 4)}-${pad(m, 2)}-${pad(d, 2)}`;
}
/** `YYYY-MM-DD` plus `n` days (n may be negative). */
export function addDays(date, n) {
    return dateOfDay(dayNumber(date) + n);
}
/** Day of the week: 0 = Sunday ... 6 = Saturday. */
export function weekdayOf(date) {
    return (((dayNumber(date) + 4) % 7) + 7) % 7;
}
export const RECURRING_FREQUENCIES = ['monthly', 'weekly'];
export const OCCURRENCE_STATES = [
    'recorded',
    'skipped',
    'overdue',
    'due_today',
    'upcoming',
];
/** The rule's due dates from `from` to `to` (both inclusive), ignoring versions and pauses. */
export function ruleDates(rule, from, to) {
    const last = rule.endOn !== null && rule.endOn < to ? rule.endOn : to;
    if (last < from)
        return [];
    const out = [];
    if (rule.frequency === 'weekly') {
        const weekday = rule.weekday ?? 0;
        let day = dayNumber(from) + ((weekday - weekdayOf(from) + 7) % 7);
        for (const end = dayNumber(last); day <= end; day += 7)
            out.push(dateOfDay(day));
        return out;
    }
    const dayOfMonth = rule.dayOfMonth ?? 1;
    for (let month = from.slice(0, 7); month <= last.slice(0, 7); month = addMonths(month, 1)) {
        const due = dueDateIn(month, dayOfMonth);
        if (due >= from && due <= last)
            out.push(due);
    }
    return out;
}
function isPaused(pauses, date) {
    return pauses.some((p) => date >= p.from && (p.to === null || date < p.to));
}
/**
 * Every occurrence from `from` to `to` (inclusive), oldest first: each version's series until
 * the next version starts, minus paused days, with the recorded and skipped marks. A mark on a
 * day the current terms no longer produce (the template was changed after) is still listed.
 */
export function buildRecurringSchedule(versions, pauses, marks, from, to, today) {
    const sorted = [...versions].sort((a, b) => a.version - b.version);
    const out = new Map();
    sorted.forEach((v, i) => {
        const next = sorted[i + 1];
        const start = v.effectiveFrom > from ? v.effectiveFrom : from;
        const end = next ? addDays(next.effectiveFrom, -1) : to;
        for (const dueOn of ruleDates(v.rule, start, end < to ? end : to)) {
            if (isPaused(pauses, dueOn))
                continue;
            out.set(dueOn, {
                dueOn,
                version: v.version,
                state: dueOn < today ? 'overdue' : dueOn === today ? 'due_today' : 'upcoming',
                transactionId: null,
                auto: false,
            });
        }
    });
    for (const m of marks) {
        if (m.dueOn < from || m.dueOn > to)
            continue;
        const version = out.get(m.dueOn)?.version ?? versionOn(sorted, m.dueOn);
        out.set(m.dueOn, {
            dueOn: m.dueOn,
            version,
            state: m.kind,
            transactionId: m.transactionId,
            auto: m.auto,
        });
    }
    return [...out.values()].sort((a, b) => (a.dueOn < b.dueOn ? -1 : a.dueOn > b.dueOn ? 1 : 0));
}
/** The version in force on `date` (the latest that started on or before it; else the first). */
function versionOn(sorted, date) {
    let found = sorted[0]?.version ?? 1;
    for (const v of sorted)
        if (v.effectiveFrom <= date)
            found = v.version;
    return found;
}
/** The occurrences still to record (not recorded or skipped). */
export function openOccurrences(schedule) {
    return schedule.filter((o) => o.state !== 'recorded' && o.state !== 'skipped');
}
//# sourceMappingURL=installments.js.map